import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tabs,
  Tab,
  Chip,
  Grid,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  Language as LanguageIcon,
  LocationOn as LocationIcon,
  Computer as ComputerIcon,
  Schedule as ScheduleIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon,
} from "@mui/icons-material";
import { purposeService, ConsentRecord, ConsentDataElement } from "../services/purpose-service";
import { useTranslations } from "../utils/translation";
import PersonalDataElement from "../components/PersonalDataElement";
import {
  initPDFDocument,
  addText,
  addSectionHeader,
  addKeyValue,
  addDivider,
  addBadge,
  addFooter,
  savePDF,
  checkPageBreak,
} from "../utils/pdf-export";

interface ConsentRecordPageProps {
  jwt: string;
  clientData: {
    subdomain: string;
    clientName: string;
  } | null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`consent-tabpanel-${index}`} aria-labelledby={`consent-tab-${index}`} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ConsentRecordPage: React.FC<ConsentRecordPageProps> = ({ jwt, clientData }) => {
  const { consentHeaderId } = useParams<{ consentHeaderId: string }>();
  const navigate = useNavigate();

  // State management
  const [consentRecord, setConsentRecord] = useState<ConsentRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  // Get translations based on consent language
  const t = useTranslations(consentRecord?.languageCode || "en");

  // Load consent record on component mount
  useEffect(() => {
    const loadConsentRecord = async () => {
      if (!consentHeaderId) {
        setError("Consent Header ID is required");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const record = await purposeService.getConsentRecord(parseInt(consentHeaderId));
        setConsentRecord(record);
      } catch (err: any) {
        setError(err.message || "Failed to load consent record");
      } finally {
        setLoading(false);
      }
    };

    if (jwt && consentHeaderId) {
      loadConsentRecord();
    }
  }, [jwt, consentHeaderId]);

  const handleBackClick = () => {
    navigate(-1); // Go back to previous page
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleExportPDF = () => {
    if (!consentRecord) return;

    const pdf = initPDFDocument("Consent Record Details");

    // Collection Point / Consent ID at the top
    pdf.yPosition = 35; // Move below title
    const headerText = `${consentRecord.collectionPoint.name} - Consent #${consentRecord.consentHeaderId}`;
    addText(pdf, headerText, 16, true, "#1B5E20");
    pdf.yPosition += 8;

    // Summary Information Box
    addText(pdf, "Report Summary", 12, true, "#2E7D32");
    pdf.yPosition += 5;

    // Calculate summary box height based on content
    const summaryBoxY = pdf.yPosition;
    const boxPadding = 8;
    const lineSpacing = 7;
    const summaryContentHeight = lineSpacing * 5 + boxPadding * 2;

    // Add summary box border first
    pdf.doc.setDrawColor("#2E7D32");
    pdf.doc.setLineWidth(0.5);
    pdf.doc.setFillColor("#F1F8F4");
    pdf.doc.roundedRect(pdf.margin, summaryBoxY, pdf.pageWidth - 2 * pdf.margin, summaryContentHeight, 2, 2, "FD");

    // Summary content inside box - use direct text placement to avoid pagination
    const summaryStartY = summaryBoxY + boxPadding;
    pdf.yPosition = summaryStartY;

    // Generated date
    pdf.doc.setFontSize(9);
    pdf.doc.setFont("helvetica", "normal");
    pdf.doc.setTextColor("#333333");
    pdf.doc.text(`Generated: ${new Date().toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}`, pdf.margin + 5, pdf.yPosition);
    pdf.yPosition += lineSpacing;

    // Consent date
    pdf.doc.text(`Consent Date: ${formatDate(consentRecord.consentDate)}`, pdf.margin + 5, pdf.yPosition);
    pdf.yPosition += lineSpacing;

    // Collection Point and Source
    pdf.doc.text(`Collection Point: ${consentRecord.collectionPoint.name} | Source: ${consentRecord.consentSource}`, pdf.margin + 5, pdf.yPosition);
    pdf.yPosition += lineSpacing;

    // Summary stats
    const purposeCount = consentRecord.purposes.length;
    const dataElementCount = consentRecord.dataElements.length;
    pdf.doc.text(`Purposes: ${purposeCount} | Data Elements: ${dataElementCount}`, pdf.margin + 5, pdf.yPosition);
    pdf.yPosition += lineSpacing;

    // Language
    pdf.doc.text(`Language: ${consentRecord.languageCode.toUpperCase()}`, pdf.margin + 5, pdf.yPosition);

    // Reset font size
    pdf.doc.setFontSize(10);

    // Position after box
    pdf.yPosition = summaryBoxY + summaryContentHeight + 8;

    // Consent Information Section
    addSectionHeader(pdf, "Consent Information");
    addKeyValue(pdf, "Consent ID", consentRecord.consentHeaderId.toString());
    addKeyValue(pdf, "Collection Point", consentRecord.collectionPoint.name);
    addKeyValue(pdf, "Collection Point ID", consentRecord.collectionPoint.identifier);
    addKeyValue(pdf, "Collection Point Type", consentRecord.collectionPoint.type);
    addKeyValue(pdf, "Language", consentRecord.languageCode.toUpperCase());
    addKeyValue(pdf, "Source", consentRecord.consentSource);

    checkPageBreak(pdf, 50);

    // Technical Details
    addSectionHeader(pdf, "Technical Details");
    addKeyValue(pdf, "IP Address", consentRecord.ipAddress);
    addKeyValue(pdf, "Location", `${consentRecord.location.city}, ${consentRecord.location.region}, ${consentRecord.location.country}`);

    // Truncate user agent if too long (similar to purpose history)
    const maxUserAgentLength = 55; // Reduced to prevent wrapping issues
    const userAgentText =
      consentRecord.userAgent.length > maxUserAgentLength
        ? consentRecord.userAgent.substring(0, maxUserAgentLength) + "..."
        : consentRecord.userAgent;
    addKeyValue(pdf, "User Agent", userAgentText);

    checkPageBreak(pdf, 60);

    // Purposes
    addSectionHeader(pdf, "Consent Purposes");
    consentRecord.purposes.forEach((purpose, index) => {
      checkPageBreak(pdf, 40);
      addText(pdf, `${index + 1}. ${purpose.purposeName}`, 11, true);
      addKeyValue(pdf, "Purpose Key", purpose.purposeKey);
      addKeyValue(pdf, "Consent Status", purpose.consentValue ? "Granted" : "Withdrawn");
      if (purpose.retentionExpirationDate) {
        addKeyValue(pdf, "Retention Expires", formatDate(purpose.retentionExpirationDate));
      }
      if (index < consentRecord.purposes.length - 1) {
        addDivider(pdf);
      }
    });

    checkPageBreak(pdf, 60);

    // Data Elements
    addSectionHeader(pdf, "Data Elements Collected");
    consentRecord.dataElements.forEach((element, index) => {
      checkPageBreak(pdf, 50);
      // Get translated name from snapshot
      const snapshotElement = consentRecord.collectionPoint.snapshot.dataElements.find((de: any) => de.id === element.dataElementId);
      const translatedName =
        snapshotElement?.translations?.find((t: any) => t.languageCode === consentRecord.languageCode)?.translatedName || element.elementName;

      addText(pdf, `${index + 1}. ${translatedName}`, 11, true);
      addKeyValue(pdf, "Element Key", element.dataElementKey);
      addKeyValue(pdf, "Data Type", element.dataElementType);
      addKeyValue(pdf, "Personal Data", element.isPersonalData ? "Yes" : "No");

      // Truncate value if too long
      const valueText =
        element.dataElementValue && element.dataElementValue.length > 100
          ? element.dataElementValue.substring(0, 100) + "..."
          : element.dataElementValue || "N/A";
      addKeyValue(pdf, "Value", valueText);

      if (index < consentRecord.dataElements.length - 1) {
        addDivider(pdf);
      }
    });

    // Privacy Policy
    if (consentRecord.privacyPolicy) {
      checkPageBreak(pdf, 50);
      addSectionHeader(pdf, "Privacy Policy");
      addText(pdf, consentRecord.privacyPolicy.title, 12, true);
      addText(pdf, consentRecord.privacyPolicy.effectiveDate, 10);
      addText(pdf, consentRecord.privacyPolicy.lastUpdated, 10);
      pdf.yPosition += 5;
      addText(pdf, "Note: Full privacy policy content is available in the online portal.", 9, false, "#666666");
    }

    // Your Rights
    if (consentRecord.yourRights) {
      checkPageBreak(pdf, 50);
      addSectionHeader(pdf, "Your Rights Under DPDPA");
      addText(pdf, consentRecord.yourRights.title, 12, true);
      addText(pdf, consentRecord.yourRights.effectiveDate, 10);
      addText(pdf, consentRecord.yourRights.lastUpdated, 10);
      pdf.yPosition += 5;
      addText(pdf, "Note: Full rights information is available in the online portal.", 9, false, "#666666");
    }

    // DPO Contact
    if (consentRecord.contactDpo) {
      checkPageBreak(pdf, 50);
      addSectionHeader(pdf, "Data Protection Officer Contact");
      addText(pdf, consentRecord.contactDpo.title, 12, true);
      addText(pdf, consentRecord.contactDpo.effectiveDate, 10);
      addText(pdf, consentRecord.contactDpo.lastUpdated, 10);
      pdf.yPosition += 5;
      addText(pdf, "Note: Full DPO contact information is available in the online portal.", 9, false, "#666666");
    }

    // Footer
    addFooter(pdf, "This document is a summary of your consent record. For complete details, visit the online portal.");

    // Download the PDF
    const fileName = `consent-record-${consentRecord.consentHeaderId}-${new Date().toISOString().split("T")[0]}.pdf`;
    savePDF(pdf, fileName);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const formatDateShort = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getConsentIcon = (consentValue: boolean) => {
    return consentValue ? <CheckCircleIcon color="success" /> : <BlockIcon color="error" />;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          {t("loading")} consent record...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBackClick} sx={{ mt: 2 }}>
          {t("goBack")}
        </Button>
      </Box>
    );
  }

  if (!consentRecord) {
    return (
      <Box p={3}>
        <Alert severity="warning">{t("notFound")}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBackClick} sx={{ mt: 2 }}>
          {t("goBack")}
        </Button>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header Section */}
      <Box mb={4}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center">
            <IconButton onClick={handleBackClick} sx={{ mr: 1 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" component="h1">
              {t("consentRecordDetails")}
            </Typography>
          </Box>
          <Tooltip title="Export consent record as PDF">
            <IconButton
              onClick={handleExportPDF}
              sx={{
                backgroundColor: "var(--bg-secondary)",
                color: "var(--text-dark)",
                border: "1px solid var(--border-light)",
                borderRadius: "var(--radius-sm)",
                "&:hover": {
                  backgroundColor: "var(--bg-accent)",
                  borderColor: "var(--primary-green)",
                  color: "var(--primary-green)",
                  transform: "translateY(-1px)",
                  boxShadow: "var(--shadow-light)",
                },
              }}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Consent Overview */}
        <Card>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  {t("consentInformation")}
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary={t("consentDate")} secondary={formatDate(consentRecord.consentDate)} />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary={t("language")}
                      secondary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <LanguageIcon fontSize="small" />
                          {consentRecord.languageCode.toUpperCase()}
                        </Box>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary={t("source")} secondary={consentRecord.consentSource} />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  {t("technicalDetails")}
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary={t("ipAddress")}
                      secondary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <ComputerIcon fontSize="small" />
                          {consentRecord.ipAddress}
                        </Box>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary={t("location")}
                      secondary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <LocationIcon fontSize="small" />
                          {consentRecord.location.city}, {consentRecord.location.region}, {consentRecord.location.country}
                        </Box>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary={t("collectionPoint")} secondary={consentRecord.collectionPoint.name} />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      {/* Tabs for different sections */}
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="consent record tabs">
          <Tab label={t("purposes")} />
          <Tab label={t("dataCollected")} />
          <Tab label={t("privacyPolicy")} />
          <Tab label={t("yourRights")} />
          <Tab label={t("contactDpo")} />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={activeTab} index={0}>
        <Typography variant="h5" gutterBottom>
          {t("consentPurposes")}
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          {t("purposesForConsent")} {formatDateShort(consentRecord.consentDate)}
        </Typography>

        <Grid container spacing={2}>
          {consentRecord.purposes.map((purpose) => (
            <Grid item xs={12} md={6} key={purpose.purposeId}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    {getConsentIcon(purpose.consentValue)}
                    <Typography variant="h6">{purpose.purposeName}</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    {t("purposeKey")}: {purpose.purposeKey}
                  </Typography>
                  {purpose.retentionExpirationDate && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <ScheduleIcon fontSize="small" color="action" />
                      <Typography variant="caption">
                        {t("retentionExpires")}: {formatDate(purpose.retentionExpirationDate)}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <Typography variant="h5" gutterBottom>
          {t("dataElementsCollected")}
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          {t("dataElementsDescription")}
        </Typography>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <LanguageIcon fontSize="small" color="action" />
          <Typography variant="caption" color="text.secondary">
            {t("fieldNamesShownIn")}: {consentRecord.languageCode.toUpperCase()}
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {consentRecord.dataElements.map((element) => {
            // Find the corresponding data element from the collection point snapshot
            const snapshotElement = consentRecord.collectionPoint.snapshot.dataElements.find((de: any) => de.id === element.dataElementId);

            // Create element with translations from snapshot
            const elementWithTranslations = {
              ...element,
              translations: snapshotElement?.translations || [],
            };

            return (
              <Grid item xs={12} md={6} key={element.dataElementId}>
                <PersonalDataElement
                  element={elementWithTranslations}
                  consentHeaderId={consentRecord.consentHeaderId}
                  languageCode={consentRecord.languageCode}
                  onPersonalDataLoaded={(vaultId, originalValue) => {
                    console.log(`Personal data loaded for vault ${vaultId}:`, originalValue);
                  }}
                />
              </Grid>
            );
          })}
        </Grid>
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        <Typography variant="h5" gutterBottom>
          Privacy Policy
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          The privacy policy that was in effect when you provided consent
        </Typography>

        {consentRecord.privacyPolicy ? (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {consentRecord.privacyPolicy.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              {consentRecord.privacyPolicy.effectiveDate} • {consentRecord.privacyPolicy.lastUpdated}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Box
              sx={{
                "& h2": { mt: 3, mb: 2, color: "primary.main" },
                "& h3": { mt: 2, mb: 1, color: "text.primary" },
                "& p": { mb: 2, lineHeight: 1.6 },
                "& ul": { mb: 2, pl: 2 },
                "& li": { mb: 1 },
              }}
              dangerouslySetInnerHTML={{ __html: consentRecord.privacyPolicy.content }}
            />
          </Paper>
        ) : (
          <Alert severity="info">No privacy policy information available for this consent record.</Alert>
        )}
      </TabPanel>

      <TabPanel value={activeTab} index={3}>
        <Typography variant="h5" gutterBottom>
          Your Rights Under DPDPA
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Your rights as a data principal under the Digital Personal Data Protection Act
        </Typography>

        {consentRecord.yourRights ? (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {consentRecord.yourRights.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              {consentRecord.yourRights.effectiveDate} • {consentRecord.yourRights.lastUpdated}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Box
              sx={{
                "& h2": { mt: 3, mb: 2, color: "primary.main" },
                "& h3": { mt: 2, mb: 1, color: "text.primary" },
                "& p": { mb: 2, lineHeight: 1.6 },
                "& ul": { mb: 2, pl: 2 },
                "& li": { mb: 1 },
              }}
              dangerouslySetInnerHTML={{ __html: consentRecord.yourRights.content }}
            />
          </Paper>
        ) : (
          <Alert severity="info">No rights information available for this consent record.</Alert>
        )}
      </TabPanel>

      <TabPanel value={activeTab} index={4}>
        <Typography variant="h5" gutterBottom>
          Data Protection Officer Contact
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Contact information for the Data Protection Officer
        </Typography>

        {consentRecord.contactDpo ? (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {consentRecord.contactDpo.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              {consentRecord.contactDpo.effectiveDate} • {consentRecord.contactDpo.lastUpdated}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Box
              sx={{
                "& h2": { mt: 3, mb: 2, color: "primary.main" },
                "& h3": { mt: 2, mb: 1, color: "text.primary" },
                "& p": { mb: 2, lineHeight: 1.6 },
                "& ul": { mb: 2, pl: 2 },
                "& li": { mb: 1 },
              }}
              dangerouslySetInnerHTML={{ __html: consentRecord.contactDpo.content }}
            />
          </Paper>
        ) : (
          <Alert severity="info">No DPO contact information available for this consent record.</Alert>
        )}
      </TabPanel>
    </Box>
  );
};

export default ConsentRecordPage;
